"use client";

import React, { useState } from "react";
import { Twitter, Linkedin, Link2, Check, Bookmark } from "lucide-react";
import { cn } from "@/lib/utils";

type ShareButtonsProps = {
  title: string;
  className?: string;
  vertical?: boolean;
};

export const ShareButtons = ({
  title,
  className,
  vertical = true,
}: ShareButtonsProps) => {
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);

  const getShareUrl = () => {
    if (typeof window !== "undefined") {
      return window.location.href;
    }
    return "";
  };

  const shareOnTwitter = () => {
    const url = getShareUrl();
    const twitterUrl = new URL("https://twitter.com/intent/tweet");
    twitterUrl.searchParams.append("text", title);
    twitterUrl.searchParams.append("url", url);
    window.open(twitterUrl.toString(), "_blank", "width=600,height=400");
  };

  const shareOnLinkedin = () => {
    const url = getShareUrl();
    const shareText = `${title}\n\n${url}`;
    const linkedinUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(shareText)}`;
    window.open(linkedinUrl, "_blank", "width=800,height=600");
  };

  const copyToClipboard = async () => {
    const url = getShareUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error(err);
    }
  };

  const btnBase =
    "w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200";
  const btnIdle = "text-white/25 hover:text-white/60 hover:bg-white/[0.06]";

  return (
    <div
      className={cn(
        "flex items-center gap-1",
        vertical ? "flex-row" : "flex-row flex-wrap justify-center",
        className,
      )}
    >
      <button
        onClick={shareOnTwitter}
        className={cn(btnBase, btnIdle)}
        aria-label="Share on Twitter"
      >
        <Twitter className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={shareOnLinkedin}
        className={cn(btnBase, btnIdle)}
        aria-label="Share on LinkedIn"
      >
        <Linkedin className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={copyToClipboard}
        className={cn(btnBase, copied ? "text-emerald-400" : btnIdle)}
        aria-label={copied ? "Copied!" : "Copy link"}
      >
        {copied ? (
          <Check className="w-3.5 h-3.5" />
        ) : (
          <Link2 className="w-3.5 h-3.5" />
        )}
      </button>
      <button
        onClick={() => setSaved(!saved)}
        className={cn(btnBase, saved ? "text-brand-1" : btnIdle)}
        aria-label={saved ? "Saved" : "Save"}
      >
        <Bookmark className={cn("w-3.5 h-3.5", saved && "fill-brand-1")} />
      </button>
    </div>
  );
};

export default ShareButtons;
